import { BorrowerContractFunnelV2Dto, ComparateurPricingStatus } from "api/pricing"
import { useMemo } from "react"

const BouncingDots = ({ dotSize = 'w-1.5 h-1.5', height = 'h-6', color = 'bg-primary/40' }: { dotSize?: string, height?: string, color?: string }) => (
  <span className={`inline-flex items-center justify-center gap-[3px] px-2 ${height}`}>
    <span className={`${dotSize} rounded-full ${color} animate-bounce [animation-delay:0ms]`} />
    <span className={`${dotSize} rounded-full ${color} animate-bounce [animation-delay:150ms]`} />
    <span className={`${dotSize} rounded-full ${color} animate-bounce [animation-delay:300ms]`} />
  </span>
)

export const useBorrowerFunnelPricing = (contractFunnel: BorrowerContractFunnelV2Dto) => {
  const pricing = contractFunnel.pricing
  const hasDiscount = !!pricing?.discount

  const isPricingLoading = contractFunnel.pricingStatus === ComparateurPricingStatus.Pending
    || contractFunnel.pricingStatus === ComparateurPricingStatus.Loading

  // const isPricingLoading = true

  const renderInitialPrice = useMemo(() => {
    if (isPricingLoading) return undefined
    if (pricing?.discount) {
      return <div className="text-base text-gray-400 line-through">
        {`${pricing.discount.initialPrice.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
      </div>
    }
    return undefined
  }, [pricing?.discount, isPricingLoading])

  const renderYearlyPrice = useMemo(() => {
    if (isPricingLoading) {
      return <BouncingDots dotSize="w-2 h-2" height="h-7 2xl:h-8" />
    }
    const yearlyPrice = pricing?.clientPrice
    return <div className={`${hasDiscount && 'text-green-600'} font-bold`}>{`${yearlyPrice?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}</div>
  }, [isPricingLoading, pricing, hasDiscount])

  const renderClientPrice = useMemo(() => {
    if (isPricingLoading) {
      return <BouncingDots dotSize="w-1 h-1" height="h-4 2xl:h-7" />
    }
    const eightYears = pricing?.totalPriceEightYears
    return eightYears?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }, [isPricingLoading, pricing?.totalPriceEightYears])

  const renderMonthlyPrice = useMemo(() => {
    if (isPricingLoading) {
      return <BouncingDots dotSize="w-1 h-1" height="h-4 2xl:h-7" />
    }
    // Utiliser monthPrice en priorité, avec fallback sur monthlyPriceIncludingTax pour la rétrocompatibilité
    const monthlyPrice = pricing?.monthPrice ?? pricing?.monthlyPriceIncludingTax
    return monthlyPrice?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }, [pricing?.monthPrice, pricing?.monthlyPriceIncludingTax, isPricingLoading])

  const renderAverageRate = useMemo(() => {
    if (isPricingLoading) {
      return <BouncingDots dotSize="w-1.5 h-1.5" height="h-6" color="bg-secondary/40" />
    }
    const averageRate = pricing?.averageRate
    if (!averageRate)
      return <></>
    return (averageRate).toFixed(4)
  }, [pricing?.averageRate, isPricingLoading])

  return {
    renderInitialPrice,
    renderYearlyPrice,
    renderClientPrice,
    renderMonthlyPrice,
    renderAverageRate,
    isPricingLoading,
  }
}
